import { JobSummaryState, jobSummariesForJobIdSelector } from './job-summary-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';
import {
  Action,
  updateResourceByParam,
  setStateDirtyByParam,
} from '../reducers/utils/state-manager';

import { SOURCE_TYPES } from '../util/application-source-types';

const InitialState: JobSummaryState = {
  jobSummariesById: {},
  jobSummariesStateByOrgId: {},
  jobTitlesById: {},
  jobTitlesStateByOrgId: {},
};

export const jobSummaryReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_JOB_SUMMARIES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobSummaries', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_JOB_SUMMARIES'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'jobSummaries', 'orgId', orgId, false, { [orgId]: data });
    }
    case FAILURE('LOAD_JOB_SUMMARIES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobSummaries', 'orgId', orgId, false);
    }
    case STARTING('LOAD_JOB_TITLE_SUMMARIES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobTitles', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_JOB_TITLE_SUMMARIES'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'jobTitles', 'orgId', orgId, false, { [orgId]: data });
    }
    case FAILURE('LOAD_JOB_TITLE_SUMMARIES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'jobTitles', 'orgId', orgId, false);
    }
    case SUCCESS('CREATE_MERGE_EMPLOYEE'):
    case SUCCESS('UPDATE_EMPLOYEE'):
    case SUCCESS('ADD_EMPLOYEE'):
    case SUCCESS('CREATE_DEMO_JOB'):
    case SUCCESS('UPDATE_JOB'): {
      const {
        args: { orgId },
        data,
      } = action.payload;

      if (orgId || data.orgId) {
        return setStateDirtyByParam(state, 'jobTitles', 'orgId', orgId || data.orgId);
      }
      return state;
    }
    case SUCCESS('LINK_CRAIGSLIST_AD'):
    case SUCCESS('CREATE_JOB_BOOST'): {
      // If we have an existing jobBoost, return current state, since job boost is already included
      if (!action.payload.data || action.payload.data.existingJobBoost) {
        return state;
      }

      const { jobBoost } = action.payload.args;
      const jobId = action.payload.data.newJobBoost?.jobId || action.payload.data.jobId;
      const amount = action.payload.data.newJobBoost?.amount || action.payload.data.amount;
      const jobSummary = jobSummariesForJobIdSelector({ jobSummaries: state }, jobId);

      // When we set up craigslist spend or a job boost, add that amount to our loaded job summary
      const field =
        action.type === SUCCESS('LINK_CRAIGSLIST_AD') ||
        (jobBoost && jobBoost.sourceType === SOURCE_TYPES.CRAIGSLIST)
          ? 'activeCraigslistSpend'
          : 'activeSocialMediaAdSpend';
      const newState = state;

      // Practically this should only be one, but being defensive here and working in the case a job appears in multiple orgs
      if (jobSummary.length) {
        jobSummary.forEach((js) => {
          // Add the new amount to the appropriate field for either of Craigslist spend or Facebook spend
          const isCraigslistOrFacebookBoost = [
            SOURCE_TYPES.CRAIGSLIST,
            SOURCE_TYPES.FACEBOOK_AD_SET,
          ].includes(jobBoost.sourceType);
          const newTotal = (js[field] || 0) + (isCraigslistOrFacebookBoost ? amount : 0);
          // Put it in the job summaries
          newState.jobSummariesById = {
            ...newState.jobSummariesById,
            [js.orgId]: {
              ...newState.jobSummariesById[js.orgId],
              [js.jobId]: {
                ...newState.jobSummariesById[js.orgId][js.jobId],
                [field]: newTotal,
              },
            },
          };
        });
      }

      return newState;
    }
    case SUCCESS('ADD_JOB'): {
      const {
        data: { job, jobSummary },
      } = action.payload;

      state.jobSummariesById = {
        ...state.jobSummariesById,
        [job.orgId]: {
          ...state.jobSummariesById[job.orgId],
          [job.job_id]: jobSummary,
        },
      };

      if (job.orgId) {
        return setStateDirtyByParam(state, 'jobTitles', 'orgId', job.orgId);
      }
      return state;
    }
    case 'SET_JOB_SUMMARIES_DIRTY':
    case SUCCESS('APPLICATION_STATUS'):
    case SUCCESS('UPDATE_JOB_MESSAGE'):
    case SUCCESS('CREATE_DEMO_APPLICANT'):
    case SUCCESS('UPDATE_TODO'): {
      const { data, args } = action.payload;
      // You can either pass in the orgId or have it come back from the server
      const orgId = args.orgId || data.orgId;
      return setStateDirtyByParam(state, 'jobSummaries', 'orgId', orgId);
    }
    // The job summary endpoint doesn't return message counts for inactive jobs,
    // so if a job is activated we need to reload summary counts.
    case SUCCESS('UPDATE_JOB_STATUS'): {
      const {
        data: { job },
      } = action.payload;

      return ['ACTIVE', 'PUBLISHED'].includes(job.status)
        ? setStateDirtyByParam(state, 'jobSummaries', 'orgId', job.orgId)
        : state;
    }
    default: {
      return state;
    }
  }
};
